import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { getSocket } from '@/hooks/useSocket'
import { formatDistanceToNow } from '@/lib/utils'
import { Heart, MessageCircle, Bookmark, Share2, Repeat2, Image as ImageIcon, Send, ChevronLeft, ChevronRight, X, User, Sparkles, Play, Volume2 } from 'lucide-react'
import ConfirmModal from '@/components/ConfirmModal'
import SafePostingModal from '@/components/SafePostingModal'
import EmptyState from '@/components/EmptyState'
import { SkeletonList } from '@/components/Skeleton'

interface Post {
  _id: string
  username: string
  userId: string | { _id: string; username: string; avatar?: string; expertType?: string; role?: string }
  message: string
  files: string[]
  tags: string[]
  likes: number
  liked: boolean
  saved: boolean
  reposted: boolean
  commentCount: number
  isAnonymous: boolean
  createdAt: string
  originalPost?: Post
}

function SelectedFilePreviews({ files, onRemove }: { files: FileList; onRemove: (idx: number) => void }) {
  const urls = useMemo(() => Array.from(files).map(file => URL.createObjectURL(file)), [files])
  useEffect(() => () => { urls.forEach(url => URL.revokeObjectURL(url)) }, [urls])

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {urls.map((url, i) => (
        <div key={i} className="relative group">
          <img src={url} alt="" className="w-16 h-16 rounded-lg object-cover border border-border" />
          <button
            type="button"
            onClick={() => onRemove(i)}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive rounded-full flex items-center justify-center text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  )
}

// F2.16: React.memo prevents re-render when post prop reference is stable
const PostCard = React.memo(function PostCard({ post, activeTab }: { post: Post; activeTab: 'new' | 'popular' }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const updatePostInCache = (updater: (p: Post) => Post) => {
    queryClient.setQueryData(['posts', activeTab], (old: any) => {
      if (!old?.pages) return old
      return {
        ...old,
        pages: old.pages.map((page: any) => ({
          ...page,
          posts: page.posts.map((p: Post) => (p._id === post._id ? updater(p) : p))
        }))
      }
    })
  }

  const likeMutation = useMutation({
    mutationFn: () => api.post(`/posts/${post._id}/like`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['posts', activeTab] })
      const previousData = queryClient.getQueryData(['posts', activeTab])
      updatePostInCache((p) => ({
        ...p,
        liked: !p.liked,
        likes: p.liked ? Math.max(0, (p.likes || 0) - 1) : (p.likes || 0) + 1
      }))
      return { previousData }
    },
    onError: (err: any, _variables, context) => {
      if (context?.previousData) queryClient.setQueryData(['posts', activeTab], context.previousData)
      toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['posts', activeTab] })
  })

  const saveMutation = useMutation({
    mutationFn: () => api.post(`/posts/${post._id}/save`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['posts', activeTab] })
      const previousData = queryClient.getQueryData(['posts', activeTab])
      updatePostInCache((p) => ({ ...p, saved: !p.saved }))
      return { previousData }
    },
    onError: (err: any, _variables, context) => {
      if (context?.previousData) queryClient.setQueryData(['posts', activeTab], context.previousData)
      toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['posts', activeTab] })
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/posts/${post._id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] })
      toast({ title: t('feed.postDeleted') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const repostMutation = useMutation({
    mutationFn: () => api.post(`/posts/${post._id}/repost`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] })
      toast({ title: t('feed.reposted') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const isAdmin = user?.role === 'admin' || user?.role === 'moderator'

  return (
    <>
      <ConfirmModal
        open={showDeleteModal}
        title={t('feed.deletePost')}
        description={t('feed.confirmDeletePost')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        variant="danger"
        onConfirm={() => { deleteMutation.mutate(); setShowDeleteModal(false) }}
        onCancel={() => setShowDeleteModal(false)}
      />
      <div className="group relative p-5 rounded-xl bg-card border border-border transition-all duration-300 hover:shadow-md">
        {/* Repost indicator */}
        {post.originalPost && (
          <div className="flex items-center gap-1.5 mb-3 text-xs text-muted-foreground">
            <Repeat2 className="w-3.5 h-3.5" />
            <span>{t('feed.repostedBy', { username: post.username })}</span>
          </div>
        )}
        {/* Author */}
        <div className="flex items-center gap-3 mb-4">
          {post.isAnonymous ? (
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-muted-foreground">
              <User className="w-5 h-5" />
            </div>
          ) : (
            <Link to={`/profile/${post.username}`} className="relative">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                {post.username[0]?.toUpperCase()}
              </div>
              {typeof post.userId === 'object' && post.userId?.expertType && (
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-primary border-2 border-background flex items-center justify-center">
                  <Sparkles className="w-2.5 h-2.5 text-primary-foreground" />
                </div>
              )}
            </Link>
          )}
          <div className="flex-1 min-w-0">
            {post.isAnonymous ? (
              <span className="text-sm font-medium text-muted-foreground">{t('common.anonymous')}</span>
            ) : (
              <Link to={`/profile/${post.username}`} className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                {post.username}
              </Link>
            )}
            <p className="text-xs text-muted-foreground">{formatDistanceToNow(post.createdAt)}</p>
          </div>
          {isAdmin && (
            <button
              type="button"
              aria-label={t('common.delete')}
              onClick={() => setShowDeleteModal(true)}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Content */}
        <p className="text-[15px] leading-relaxed text-foreground mb-4 whitespace-pre-wrap">
          {post.message}
        </p>

        {/* Media Grid */}
        {post.files?.length > 0 && (
          <div className={`grid gap-2 mb-4 ${post.files.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {post.files.map((file, i) => {
              const isVideo = /\.(mp4|webm|mov)(\?|$)/i.test(file);
              const isAudio = /\.(mp3|wav|ogg|m4a)(\?|$)/i.test(file);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => { setLightboxIndex(i); setLightboxOpen(true) }}
                  className={`relative overflow-hidden rounded-lg group/img ${post.files.length === 1 ? 'aspect-video' : 'aspect-square'} ${isAudio ? 'aspect-auto h-20' : ''}`}
                >
                  {isVideo ? (
                    <>
                      <video src={file} className="w-full h-full object-cover" preload="metadata" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Play className="w-8 h-8 text-white" />
                      </div>
                    </>
                  ) : isAudio ? (
                    <div className="w-full h-full flex items-center gap-3 px-4 bg-secondary">
                      <Volume2 className="w-5 h-5 text-primary" />
                      <span className="text-sm text-muted-foreground">Ses dosyası</span>
                    </div>
                  ) : (
                    <img
                      src={file}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-105"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover/img:opacity-100 transition-opacity" />
                </button>
              );
            })}
          </div>
        )}

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.map(tag => (
              <Link key={tag} to={`/search?q=${encodeURIComponent(tag)}`}>
                <span className="text-xs px-3 py-1 rounded-full bg-secondary border border-border text-primary hover:bg-primary/10 transition-colors">
                  #{tag}
                </span>
              </Link>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 pt-4 border-t border-border">
          <button
            type="button"
            onClick={() => likeMutation.mutate()}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              post.liked ? 'text-red-500 bg-red-500/10' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
          >
            <Heart className={`w-4 h-4 ${post.liked ? 'fill-current' : ''}`} />
            <span>{post.likes || 0}</span>
          </button>

          <Link to={`/post/${post._id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
            <MessageCircle className="w-4 h-4" />
            <span>{post.commentCount || 0}</span>
          </Link>

          <button
            type="button"
            onClick={() => repostMutation.mutate()}
            disabled={repostMutation.isPending}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              post.reposted ? 'text-accent bg-accent/10' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
          >
            <Repeat2 className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              post.saved ? 'text-amber-500 bg-amber-500/10' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
          >
            <Bookmark className={`w-4 h-4 ${post.saved ? 'fill-current' : ''}`} />
          </button>

          <button
            type="button"
            onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/post/${post._id}`); toast({ title: t('feed.linkCopied') }) }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors ml-auto"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && post.files && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
          <button type="button" className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors" onClick={() => setLightboxOpen(false)}>
            <X className="w-6 h-6" />
          </button>
          {post.files.length > 1 && lightboxIndex > 0 && (
            <button type="button" className="absolute left-6 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors" onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i - 1) }}>
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {(() => {
            const file = post.files[lightboxIndex];
            const isVideo = /\.(mp4|webm|mov)(\?|$)/i.test(file);
            const isAudio = /\.(mp3|wav|ogg|m4a)(\?|$)/i.test(file);
            if (isVideo) {
              return <video src={file} controls autoPlay className="max-w-[90vw] max-h-[85vh] rounded-lg" onClick={(e) => e.stopPropagation()} />;
            }
            if (isAudio) {
              return (
                <div className="bg-card p-8 rounded-lg flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
                  <Volume2 className="w-12 h-12 text-primary" />
                  <audio src={file} controls autoPlay className="w-64" />
                </div>
              );
            }
            return <img src={file} alt="" className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />;
          })()}
          {post.files.length > 1 && lightboxIndex < post.files.length - 1 && (
            <button type="button" className="absolute right-6 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors" onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i + 1) }}>
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
          {post.files.length > 1 && (
            <div className="absolute bottom-6 flex gap-1.5">
              {post.files.map((_, i) => (
                <button key={i} type="button" onClick={(e) => { e.stopPropagation(); setLightboxIndex(i) }} className={`h-1.5 rounded-full transition-all ${i === lightboxIndex ? 'bg-white w-4' : 'bg-white/40 w-1.5'}`} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
});

export default function Feed() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [message, setMessage] = useState('')
  const [tags, setTags] = useState('')
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null)
  const [activeTab, setActiveTab] = useState<'new' | 'popular'>('new')
  const [composeFocused, setComposeFocused] = useState(false)
  const [safeModal, setSafeModal] = useState<{ open: boolean; ephemeralChatId?: string; status?: 'removed' | 'shadow' | 'review' | 'safe'; reason?: string }>({ open: false })
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['posts', activeTab],
    queryFn: async ({ pageParam }) => {
      const sort = activeTab === 'popular' ? 'popular' : 'new'
      const res = await api.get(`/posts?page=${pageParam}&limit=12&sort=${sort}`)
      return res.data
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => lastPage.posts?.length === 12 ? allPages.length + 1 : undefined
  })

  useEffect(() => {
    const el = loadMoreRef.current
    if (!el || !hasNextPage) return
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting && !isFetchingNextPage) fetchNextPage() },
      { rootMargin: '300px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    const socket = getSocket()
    if (!socket) return
    const handleNewPost = (post: Post) => {
      queryClient.setQueryData(['posts', activeTab], (old: any) => {
        if (!old) return old
        return { ...old, pages: old.pages.map((page: any, idx: number) => idx === 0 ? { ...page, posts: [post, ...page.posts] } : page) }
      })
    }
    const handleEphemeralInvite = (data: { chatId: string; reason: string; postId: string }) => {
      setSafeModal({ open: true, ephemeralChatId: data.chatId, status: 'shadow', reason: data.reason })
    }
    socket.on('post:new', handleNewPost)
    socket.on('ephemeral:invitation', handleEphemeralInvite)
    return () => {
      socket.off('post:new', handleNewPost)
      socket.off('ephemeral:invitation', handleEphemeralInvite)
    }
  }, [queryClient, activeTab])

  const allPosts = data?.pages?.flatMap((page: any) => page.posts || []) || []

  const createPost = useMutation({
    mutationFn: async () => {
      const formData = new FormData()
      formData.append('message', message)
      formData.append('tags', tags)
      formData.append('isAnonymous', String(isAnonymous))
      if (selectedFiles) {
        Array.from(selectedFiles).forEach(file => formData.append('files', file))
      }
      return api.post('/posts', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    onSuccess: (res: any) => {
      setMessage(''); setTags(''); setSelectedFiles(null); setIsAnonymous(false); setComposeFocused(false)
      queryClient.invalidateQueries({ queryKey: ['posts'] })

      // Handle AI moderation result
      const mod = res.data?._moderation
      if (mod?.ephemeralChatId) {
        // Crisis detected — show safe posting modal with ephemeral invitation
        setSafeModal({
          open: true,
          ephemeralChatId: mod.ephemeralChatId,
          status: 'shadow',
          reason: mod.reason,
        })
      } else {
        toast({ title: t('feed.postCreated') })
      }
    },
    onError: (err: any) => {
      const code = err.response?.data?.code
      const error = err.response?.data?.error || t('common.errorOccurred')

      if (code === 'CONTENT_BLOCKED') {
        // Hard-blocked: clear the form so the user sees the post was NOT created
        setMessage(''); setTags(''); setSelectedFiles(null); setIsAnonymous(false); setComposeFocused(false)
        setSafeModal({
          open: true,
          status: 'removed',
          reason: error,
        })
      } else {
        toast({ title: error, variant: 'destructive' })
      }
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return
    createPost.mutate()
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Compose */}
      {user?.canPost === false ? (
        <div className="p-5 rounded-xl bg-destructive/5 border border-destructive/20 text-destructive text-sm mb-8">
          {t('feed.postRestrictionMessage')}
        </div>
      ) : (
        <div className={`mb-8 p-5 rounded-xl transition-all duration-300 ${
          composeFocused ? 'bg-card border border-primary/30 shadow-sm' : 'bg-card border border-border'
        }`}>
          <form onSubmit={handleSubmit}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary shrink-0">
                {user?.username?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onFocus={() => setComposeFocused(true)}
                  onBlur={() => { if (!message && !tags && !selectedFiles) setComposeFocused(false) }}
                  placeholder={t('feed.placeholder')}
                  className="w-full bg-transparent resize-none outline-none text-[15px] text-foreground placeholder:text-muted-foreground min-h-[60px]"
                  maxLength={2000}
                />
                {composeFocused && (
                  <div className="mt-3">
                    <input
                      type="text"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder={t('feed.tagsPlaceholder') || '#tag1, #tag2'}
                      className="w-full bg-transparent border-b border-border pb-2 mb-3 text-sm text-muted-foreground placeholder:text-muted-foreground outline-none focus:border-primary transition-colors"
                    />
                  </div>
                )}
                {selectedFiles && selectedFiles.length > 0 && (
                  <SelectedFilePreviews files={selectedFiles} onRemove={(idx) => {
                    const dt = new DataTransfer()
                    Array.from(selectedFiles).filter((_, i) => i !== idx).forEach(f => dt.items.add(f))
                    setSelectedFiles(dt.files.length > 0 ? dt.files : null)
                  }} />
                )}
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div className="flex items-center gap-2">
                    <label className="p-2.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors cursor-pointer" aria-label={t('feed.addImage')}>
                      <ImageIcon className="w-5 h-5" />
                      <input type="file" multiple className="hidden" onChange={(e) => {
                        const files = e.target.files
                        if (files) {
                          const oversized = Array.from(files).filter(f => f.size > 10 * 1024 * 1024)
                          if (oversized.length > 0) { toast({ title: t('feed.fileTooLarge'), variant: 'destructive' }); return }
                        }
                        setSelectedFiles(files)
                        setComposeFocused(true)
                      }} accept="image/*" />
                    </label>
                    <label className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors cursor-pointer">
                      <input type="checkbox" checked={isAnonymous} onChange={(e) => setIsAnonymous(e.target.checked)} className="accent-primary" />
                      {t('feed.anonymous')}
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={!message.trim() || createPost.isPending}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-40"
                  >
                    <Send className="w-4 h-4" />
                    {createPost.isPending ? t('common.sending') : t('feed.post')}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-3 mb-8">
        {[
          { key: 'new', label: t('feed.new') },
          { key: 'popular', label: t('feed.popular') },
        ].map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key as any)}
            className={`relative px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              activeTab === tab.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Posts */}
      {isLoading ? (
        <SkeletonList count={3} />
      ) : (
        <div className="space-y-6">
          {allPosts.map((post: Post) => (
            <div key={post._id}>
              <PostCard post={post} activeTab={activeTab} />
            </div>
          ))}
          {allPosts.length === 0 && (
            <EmptyState
              icon={MessageCircle}
              description={t('feed.empty')}
            />
          )}
          <div ref={loadMoreRef} className="py-8 flex justify-center">
            {isFetchingNextPage && (
              <div className="w-6 h-6 rounded-full border-2 border-muted border-t-primary animate-spin" />
            )}
          </div>
        </div>
      )}

      <SafePostingModal
        open={safeModal.open}
        onClose={() => setSafeModal({ open: false })}
        ephemeralChatId={safeModal.ephemeralChatId}
        moderationStatus={safeModal.status}
        reason={safeModal.reason}
        onOpenChat={(chatId) => {
          setSafeModal({ open: false })
          navigate(`/ephemeral-support/${chatId}`)
        }}
      />
    </div>
  )
}
